import { DeleteStages } from 'constants/deleteStages';
import { Resources } from 'constants/resources';

import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { useStagedDelete } from 'hooks/useStagedDelete';
import { queryClient } from 'providers/queryClient';
import { useNotify, useRedirect } from 'react-admin';
import { CancelButton } from 'shared/mui/Buttons';
import { DeleteStageDialog } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialog';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { User } from 'types/embedded/user';

type UserDeleteModalProps = {
  handleCloseRecordModal: () => void;
  openRecordModal: boolean;
  record?: User;
};

export const UsersDeleteModal = ({
  handleCloseRecordModal,
  openRecordModal,
  record,
}: UserDeleteModalProps) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const { toRoot } = useNavigateToRoot();

  const { deleteOneRecord, denyDeleteReason, stage, setStage } = useStagedDelete({
    record,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [Resources.Users] });
      handleCloseRecordModal();
      setStage(DeleteStages.Initial);
      redirect('list', Resources.Users);
      notify(
        <>
          Пользователь <TypographyBold text={`${record?.firstName} ${record?.lastName}`} /> удалён
        </>,
        { type: 'success' },
      );
    },
  });

  const handleCloseDeleteDialog = () => {
    setStage(DeleteStages.Initial);
    handleCloseRecordModal();
  };

  const returnToUsersList = () => {
    handleCloseDeleteDialog();
    toRoot();
  };

  return (
    <DeleteStageDialog
      deleteOneRecord={deleteOneRecord}
      details={
        <Typography>
          Пользователь{' '}
          <TypographyBold fontSize={16} text={`${record?.firstName} ${record?.lastName}`} /> будет
          удален без возможности восстановления.
        </Typography>
      }
      onClose={handleCloseDeleteDialog}
      open={openRecordModal}
      stage={stage}
      title="Удаление пользователя"
    >
      <DeleteStageDialogDenyReason reason={denyDeleteReason}>
        <CancelButton
          key="history-back-button"
          label="Вернуться к списку пользователей"
          onClick={returnToUsersList}
          sx={{ marginLeft: 'auto' }}
          variant="outlined"
        />
      </DeleteStageDialogDenyReason>
    </DeleteStageDialog>
  );
};
